import { createFileRoute } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Play, CheckCircle, XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { format, startOfMonth, subMonths } from "date-fns";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Header } from "@/components/universal/header";
import {
  getListReportJobsQueryOptions,
  useTriggerReportJob,
} from "@/requests/gen/react-query/report";
import { JobInfo } from "@/requests/gen/react-query/fastAPI.schemas";
import snakeToCamel from "@/lib/snake-to-camel";
import { suspenseOptions } from "@/lib/suspense-query";

function getReportMonthOptions() {
  return Array.from({ length: 12 }, (_, i) => {
    const month = startOfMonth(subMonths(new Date(), i + 1));
    return {
      value: format(month, "yyyy-MM-dd"),
      label: format(month, "MMMM yyyy"),
    };
  });
}

const REPORT_MONTHS = getReportMonthOptions();

export const Route = createFileRoute("/_private/_admin/jobs")({
  loader: ({ context: { queryClient } }) =>
    queryClient.ensureQueryData(getListReportJobsQueryOptions()),
  component: JobsPage,
});

type JobStatus = "idle" | "running" | "success" | "error";
type JobKey = JobInfo["key"];
type JobCategory = JobInfo["category"];

// Section order only. Which job belongs to which category lives on the backend.
const CATEGORY_ORDER: JobCategory[] = [
  "report",
  "notification",
  "operation",
  "payment",
];

function groupByCategory(jobs: JobInfo[]) {
  const groups = new Map<JobCategory, JobInfo[]>();
  for (const job of jobs) {
    const existing = groups.get(job.category);
    if (existing) existing.push(job);
    else groups.set(job.category, [job]);
  }
  const known = CATEGORY_ORDER.filter((category) => groups.has(category));
  const extra = [...groups.keys()].filter(
    (category) => !CATEGORY_ORDER.includes(category),
  );
  return [...known, ...extra].map((category) => ({
    category,
    jobs: groups.get(category)!.sort((a, b) => a.key.localeCompare(b.key)),
  }));
}

function JobRow({
  jobKey,
  status,
  onRun,
  children,
}: {
  jobKey: JobKey;
  status: JobStatus;
  onRun: () => void;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between py-3">
      <span className="text-sm font-medium">{snakeToCamel(jobKey)}</span>
      <div className="flex items-center gap-2">
        {status === "success" && (
          <CheckCircle className="size-4 text-success" />
        )}
        {status === "error" && <XCircle className="size-4 text-destructive" />}
        {children}
        <Button
          size="sm"
          variant="outline"
          disabled={status === "running"}
          onClick={onRun}
        >
          {status === "running" ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Play className="size-4" />
          )}
          Run
        </Button>
      </div>
    </div>
  );
}

function JobsPage() {
  const [jobStatuses, setJobStatuses] = useState<
    Partial<Record<JobKey, JobStatus>>
  >({});
  const [reportMonths, setReportMonths] = useState<
    Partial<Record<JobKey, string>>
  >({});

  const { data: jobs } = useSuspenseQuery(
    suspenseOptions(getListReportJobsQueryOptions()),
  );
  const triggerJob = useTriggerReportJob();

  function handleTrigger(jobKey: JobKey, category: JobCategory) {
    const label = snakeToCamel(jobKey);
    const params =
      category === "report"
        ? { report_month: reportMonths[jobKey] ?? REPORT_MONTHS[0].value }
        : undefined;
    setJobStatuses((prev) => ({ ...prev, [jobKey]: "running" }));
    triggerJob.mutate(
      { jobKey, params },
      {
        onSuccess: (result) => {
          setJobStatuses((prev) => ({
            ...prev,
            [jobKey]: result.success ? "success" : "error",
          }));
          if (result.success) {
            toast.success(`${label} completed successfully.`);
          } else {
            toast.error(`${label} failed.`);
          }
        },
        onError: () => {
          setJobStatuses((prev) => ({ ...prev, [jobKey]: "error" }));
          toast.error(`${label} failed.`);
        },
      },
    );
  }

  return (
    <div>
      <title>Scheduled Jobs - Legrande Health Portal</title>
      <Header crumbs={[{ link: { to: "/jobs" }, name: "Scheduled Jobs" }]} />
      <Card>
        <CardHeader>
          <CardTitle>Scheduled Jobs</CardTitle>
          <CardDescription>
            Manually run scheduled jobs on demand.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {groupByCategory(jobs).map(({ category, jobs: categoryJobs }) => (
            <Card key={category} className="gap-0">
              <CardHeader>
                <CardTitle className="text-sm uppercase tracking-wide text-muted-foreground">
                  {snakeToCamel(category)}s
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="ml-4 divide-y">
                  {categoryJobs.map(({ key }) => (
                    <JobRow
                      key={key}
                      jobKey={key}
                      status={jobStatuses[key] ?? "idle"}
                      onRun={() => handleTrigger(key, category)}
                    >
                      {category === "report" && (
                        <Select
                          value={reportMonths[key] ?? REPORT_MONTHS[0].value}
                          onValueChange={(value) =>
                            setReportMonths((prev) => ({
                              ...prev,
                              [key]: value,
                            }))
                          }
                        >
                          <SelectTrigger size="sm" className="w-40">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {REPORT_MONTHS.map((month) => (
                              <SelectItem key={month.value} value={month.value}>
                                {month.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </JobRow>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
